/**
 * Whether a project's preview follows the viewport its source was drawn at, or
 * lets the creator move between breakpoints.
 *
 * This is a design-system decision at project level, not a preview toggle: an
 * app is responsive or it is not, and that fact outlives the tab it is looked
 * at in. It therefore lives in the project manifest — beside
 * `manifest.studio.viewport` and the `sourceConversion` frames it overrides —
 * rather than in the session-local axis store, which is not persisted per
 * project.
 *
 * Storing it in the manifest means a read-modify-write through
 * `saveProjectMeta`, which replaces the manifest field whole and carries no
 * clobber guard (the one in `project-api` covers `settings`, not `manifest`).
 * Operator decision, recorded here so nobody re-litigates it from the code
 * alone: this deployment is a local network, so two editors racing one
 * manifest is not a real case and no guard was added for it. That reasoning
 * stops holding the day this is multi-tenant.
 */
export type ViewportPolicy = 'source-fixed' | 'responsive'

const DECLARED: readonly ViewportPolicy[] = ['source-fixed', 'responsive']

function readRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined
}

/**
 * The project's own decision, or `null` when it has not made one. A value
 * outside the declared set is not a decision — it is a typo or a newer writer,
 * and either way the honest answer is that this project has not decided.
 */
export function readViewportPolicy(project: unknown): ViewportPolicy | null {
  const manifest = readRecord(readRecord(project)?.manifest)
  const studio = readRecord(manifest?.studio)
  const raw = studio?.viewportPolicy
  return DECLARED.includes(raw as ViewportPolicy) ? (raw as ViewportPolicy) : null
}

/**
 * Whether the preview should be held at the source's viewport.
 *
 * Absent policy keeps the behaviour projects have today — they were pinned, and
 * a project nobody has decided about stays pinned. `responsive` is the only
 * thing that releases it, and nothing pins when there is no source viewport to
 * pin to.
 */
export function viewportIsPinnedToSource(
  project: unknown,
  hasSourceViewport: boolean,
): boolean {
  if (!hasSourceViewport) return false
  return readViewportPolicy(project) !== 'responsive'
}
